import { useInfiniteQuery } from "react-query";
import { axiosInstance } from "./axios";
import { IOrderObject } from "components/SortOrder/typings";
import { IGenericResponse } from "dto/IGenericResponse";
import { IPackage } from "dto/IPackage";
import { IError } from "dto/IError";
import { IEncryptedGenericResponse } from "dto/IEncryptedGenericResponse";
import useCustomMutation from "hooks/useCustomMutation";
import useCustomQuery from "hooks/useCustomQuery";
import { useTrashConfirmModalStore } from "store/trashConfirm.store";
import { ItemKind } from "dto/IKindItems";

export interface IGetListOfMessages {
  FilterId?: string;
  CaseId?: string;
  Kind?: number;
  Page: number;
  Group: boolean;
  IsIncoming: boolean;
  SortProp: "Date" | "ReceiveDate" | "Deadline" | "SendDate";
  SortOrder: "Ascending" | "Descending";
  Text?: string | null;
  ThreadId?: string;
  enabled?: boolean;
  Desk?: boolean;
}

const queryKeys = {
  getPackage: (payload: IGetPackage, filters?: IOrderObject) => [
    "listService.getPackage",
    payload,
    filters,
  ],
  getListOfMessages: (payload: IGetListOfMessages) => [
    "listService.getListOfMessages",
    payload,
  ],
  getListOfPackages: (payload: IGetListOfMessages) => [
    "listService.getListOfPackages",
    payload,
  ],
  getMetaData: (filterId: string) => ["listService.getMetaData", filterId],
};

export interface IGetPackage {
  CaseId?: string;
  FilterId?: string;
  ThreadId?: string;
  Kind?: ItemKind;
  GroupThreads?: boolean;
  IncludeRemoved?: boolean;
  enabled?: boolean;
}

const getPackage = (
  {
    CaseId,
    FilterId,
    Kind,
    ThreadId,
    GroupThreads,
    IncludeRemoved,
  }: IGetPackage,
  filters?: IOrderObject,
): Promise<{ data: IEncryptedGenericResponse<IPackage[]> }> => {
  const query = new URLSearchParams(window.location.search);
  if (FilterId?.length) {
    query.append("Id", FilterId);
  }

  if (CaseId?.length) {
    query.append("CaseId", CaseId);
  }

  if (Kind) {
    query.append("Kind", Kind.valueOf().toString());
  }

  if (GroupThreads != undefined) {
    query.append("GroupThreads", GroupThreads.toString());
  }

  if (IncludeRemoved != undefined) {
    query.append("IncludeRemoved", IncludeRemoved.toString());
  }

  if (ThreadId?.length) {
    query.append("ThreadId", ThreadId);
  }

  if (filters) {
    query.append("SortProp", filters.type);
    query.append("SortOrder", filters.order);
  }

  // if(!CaseId && !FilterId && !threadId) {
  //   return {
  //     data: [],

  //   } as AxiosResponse
  // }

  return axiosInstance.get(`/list?${query}`);
};

const useGetPackage = (payload: IGetPackage, filters?: IOrderObject) => {
  return useCustomQuery(
    queryKeys.getPackage(payload, filters),
    () => getPackage(payload, filters),
    {
      keepPreviousData: false,
      enabled: payload.enabled,
    },
  );
};

const getListOfMessages = async ({
  CaseId,
  FilterId,
  Page,
  Group,
  IsIncoming,
  SortProp,
  SortOrder,
  Text,
  ThreadId,
  Kind,
  Desk,
}: IGetListOfMessages): Promise<IGenericResponse<IPackage[]>> => {
  const query = new URLSearchParams(window.location.search);

  if (Text) {
    query.append("Text", Text);
  }
  if (FilterId) {
    query.append("FilterId", FilterId);
  }

  if (Desk) {
    query.append("Desk", Desk.toString());
  }

  if (Kind) {
    query.append("Kind", Kind.toString());
  }
  query.append("Page", Page.toString());
  query.append("Group", Group.toString());
  query.append("IsIncoming", IsIncoming.toString());
  query.append("SortProp", SortProp);
  query.append("SortOrder", SortOrder);

  if (CaseId?.length) {
    query.append("CaseId", CaseId);
  }

  if (ThreadId) {
    query.append("ThreadId", ThreadId);
  }

  const { data } = await axiosInstance.get(`/list?${query}`);

  return data;
};

const useGetListOfMessages = (payload: IGetListOfMessages) => {
  return useCustomQuery(
    queryKeys.getListOfMessages(payload),
    () => getListOfMessages(payload),
    {
      keepPreviousData: false,
      enabled: payload.enabled,
    },
  );
};

const getListOfPackage = async (
  {
    CaseId,
    FilterId,
    Group,
    IsIncoming,
    SortProp,
    SortOrder,
    Text,
    Kind,
    Desk,
  }: IGetListOfMessages,
  pageParam: number,
) => {
  const query = new URLSearchParams(window.location.search);

  if (Text) {
    query.append("Text", Text);
  }
  if (FilterId) {
    query.append("FilterId", FilterId);
  }

  if (Kind) {
    query.append("Kind", Kind.toString());
  }

  if (Desk) {
    query.append("Desk", Desk.toString());
  }

  query.append("Page", pageParam.toString());
  query.append("Group", Group.toString());
  query.append("IsIncoming", IsIncoming.toString());
  query.append("SortProp", SortProp);
  query.append("SortOrder", SortOrder);

  if (CaseId?.length) {
    query.append("CaseId", CaseId);
  }

  const { data } = await axiosInstance.get(`/list?${query}`);

  return data;
};

const useGetListOfPackages = (payload: IGetListOfMessages) => {
  return useInfiniteQuery(
    ["projects"],
    async ({ pageParam = 0 }) => getListOfPackage(payload, pageParam),
    {
      getNextPageParam: () => {
        //
        const nextPage = 1;
        return nextPage;
      },
      enabled: payload.enabled,
    },
  );
};

interface IItem {
  Id: string;
  Kind: number;
}
export interface IDelete {
  Items: IItem[];
}

const deleteList = (payload: IDelete): Promise<{ data: IError }> => {
  return axiosInstance.post("List/trash", payload);
};

const useDeleteList = () => {
  return useCustomMutation(deleteList, {
    // Po przeniesieniu przesyłki do kosza pokaż globalny modal potwierdzający
    // (chyba że użytkownik wybrał „Nie pokazuj ponownie").
    onSuccess: () => useTrashConfirmModalStore.getState().open(),
  });
};

export interface IDossierAttachmentFolderLink {
  AttachmentId: string;
  FolderId?: string;
}

export interface IManageDossier {
  AttachmentIds: string[];
  OwnerId: string;
  OwnerKind: number;
  Show: boolean;
  Links?: IDossierAttachmentFolderLink[];
}

const manageDossier = (payload: IManageDossier): Promise<{ data: IError }> => {
  return axiosInstance.post("list/dossier", payload);
};

const useManageDossier = () => {
  return useCustomMutation(manageDossier);
};

const getMetaData = async (
  filterId: string,
  isEnabled: boolean,
): Promise<{ Name: string; List: number; Id: string; ItemKinds: number }> => {
  if (!isEnabled) return null;

  if (!filterId.length || filterId === "sending" || filterId === "all") {
    return {
      Id: "-1",
      Name: "",
      List: -1,
      ItemKinds: ItemKind.All,
    };
  }

  const r = await axiosInstance.get(`/list/filter?id=${filterId}`);

  return r?.data ?? [];
};

const useGetMetaData = (filterId: string, isEnabled: boolean) => {
  return useCustomQuery(queryKeys.getMetaData(filterId), () =>
    getMetaData(filterId, isEnabled),
  );
};

export interface IRestorePackage {
  Items: {
    Id: string;
    Kind: number;
  }[];
}

const restorePackage = (
  payload: IRestorePackage,
): Promise<{ data: IError }> => {
  return axiosInstance.post("/list/restoreFromTrash", payload);
};

const useRestorePackage = () => {
  return useCustomMutation(restorePackage);
};

// Opróżnienie kosza przesyłek — trwałe usunięcie usuniętych przesyłek bieżącego profilu.
const emptyTrash = (): Promise<{ data: IError }> => {
  return axiosInstance.post("/list/trash/empty", {});
};

const useEmptyTrash = () => {
  return useCustomMutation(emptyTrash);
};

export default {
  useGetPackage,
  useGetListOfMessages,
  useDeleteList,
  useGetListOfPackages,
  useManageDossier,
  useGetMetaData,
  useRestorePackage,
  useEmptyTrash,
};
